import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import {
  MatLegacyTabChangeEvent as MatTabChangeEvent,
  MatLegacyTabGroup as MatTabGroup,
} from '@angular/material/legacy-tabs';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Interface
import { TabsInterface } from 'v2/shared/patterns/tabs';

// CONFIG
import {
  ClientApiService,
  ClientShippingApiService,
  OptionInterface,
} from '../../../shared';
import {
  CLIENT_PROFILE_TABS,
  EClientProfileTab,
} from '../../client-profile.config';

// Services

@Component({
  selector: 'lum-client-profile-dialog',
  templateUrl: './client-profile-dialog.component.html',
  styleUrls: ['./client-profile-dialog.component.scss'],
})
export class ClientProfileDialogComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  tab :string = EClientProfileTab.CLIENT_INFO;
  tabs :TabsInterface[] = [];
  hasShippingAddress :boolean = false;
  shippingAddress$ :Observable<OptionInterface[]>;

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :any,
    private clientApiService :ClientApiService,
    private clientShippingApiService :ClientShippingApiService,
    private dialogRef :MatDialogRef<ClientProfileDialogComponent>,
    private fb :UntypedFormBuilder,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.initTabs();
    this.initClient();
    this.getDataFromService();
    this.setBusinessAccountId();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  selectedTab(evt :MatTabChangeEvent) :void {
    this.tab = this.tabs[evt.index]?.name;
  }
  onChangeTab(tabGroup :MatTabGroup, tab :any) :void {
    this.tab = tab?.name;
    tabGroup.selectedIndex = tab?.index;
  }
  onCheckTabValidation(event :any) :void {
    const index = this.tabs.findIndex((t) => t.name === event.nextTab);
    this.tabs[index].disabled = event.invalid;
  }
  onSaveShippingAddress(close :boolean) :void {
    if (close) {
      this.hasShippingAddress = true;
    }
  }
  onClose() :void {
    this.clientApiService.setRecords([]);
    this.dialogRef.close();
  }

  private initForm() :void {
    this.form = this.fb.group({
      business_account_id: [null],
      business_name: ['', Validators.required],
      fein: ['', Validators.maxLength(10)],
      business_type: [''],
      business_type_id: [null],
      salesRepAssignee: [[]],
      notes: [''],
      tags: [[]],
      business_address: ['', Validators.required],
      business_address_line_2: [''],
      business_city: ['', Validators.required],
      business_state: ['', Validators.required],
      business_zip: ['', Validators.required],
      business_country: ['US', Validators.required],
      client_grade: [''],
      phone: ['', Validators.required],
      email: [''],
      firstname: [''],
      lastname: [''],
      shipping_address: ['', Validators.required],
      shipping_address_line_2: [''],
      shipping_city: ['', Validators.required],
      shipping_state: ['', Validators.required],
      shipping_zip: ['', Validators.required],
      shipping_country: ['US', Validators.required],
      same_with_business: [false],
    });
  }
  private initClient() :void {
    if (this.data.client) {
      this.form.patchValue({
        ...this.data.client,
        business_account_id: this.data.client?.id,
      });
    }
  }
  private initTabs() :void {
    const tabs = CLIENT_PROFILE_TABS;
    this.tabs = tabs.map((tab) => ({
      ...tab,
      disabled: tab.name !== EClientProfileTab.CLIENT_INFO,
    }));
  }
  private getDataFromService() :void {
    this.shippingAddress$ = this.clientShippingApiService.addresses$;
  }
  private setBusinessAccountId() :void {
    this.clientApiService.clients$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((record :any) => {
        if (record?.id) {
          this.form.patchValue({
            ...record,
            business_account_id: record?.id,
          });
        }
      });
  }

  get mode() :string {
    return this.data?.client ? 'Update' : 'Create';
  }
}
